/**
 * Creating or renaming a menu category, as a page.
 *
 * This was a dialog. A dialog renders through a portal, outside the screen that
 * knows how much of the viewport the on-screen keyboard covers, so its backdrop,
 * its height and the field being typed into each have to be corrected by hand —
 * and it is still the wrong container for something a merchant fills in on a
 * phone. A page gets that from `Screen`, once.
 *
 * Creating and renaming are one screen because they are one form: the route
 * decides which, and the only differences are the heading and whether a
 * `sort_order` is assigned.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CircleHelp } from 'lucide-react-native';
import { createCategory, fetchMerchantMenu, updateCategory } from '@yummy/db';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  Stack,
  Text,
  TextField,
  useAction,
  useAsync,
  useLocale,
  useTheme,
  useToast,
} from '@yummy/ui-rnr';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';

/**
 * Renders the category form.
 *
 * @returns The form page.
 */
export function CategoryFormScreen() {
  const { categoryId } = useParams<{ categoryId?: string }>();
  const { t } = useLocale();
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const isNew = categoryId === undefined;
  const action = useAction();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const menu = useAsync(
    async () => (restaurantId ? await fetchMerchantMenu(supabase, restaurantId) : null),
    [restaurantId],
  );

  const categories = useMemo(() => menu.data?.categories ?? [], [menu.data]);
  const existing = isNew ? undefined : categories.find((c) => c.id === categoryId);

  // A rename reached by deep link has nowhere else to read the current values
  // from, so they are seeded once the list arrives rather than only being passed
  // in by the list screen.
  useEffect(() => {
    if (!existing) return;
    setName(existing.name);
    setDescription(existing.description ?? '');
  }, [existing]);

  // Back to the category this was opened from, not to the whole menu. Without
  // the category the list comes back on All and the merchant has to find their
  // place again. Read from the URL rather than passed down, so it survives a
  // reload of the form itself.
  const [searchParams] = useSearchParams();
  const returnCategory = searchParams.get('category');
  const back = useCallback(
    () => navigate(returnCategory ? `/menu?category=${returnCategory}` : '/menu'),
    [navigate, returnCategory],
  );

  const save = useCallback(async () => {
    const trimmed = name.trim();
    if (!restaurantId || !trimmed) return;

    const result = await action.run(() =>
      existing
        ? updateCategory(supabase, existing.id, {
            name: trimmed,
            description: description.trim() || null,
          })
        : createCategory(supabase, restaurantId, {
            name: trimmed,
            description: description.trim() || null,
            sort_order: categories.length,
          }),
    );

    if (result) {
      toast.show(t('merchant.menu.categorySaved'), { tone: 'success' });
      // Land on the category just saved rather than the whole menu. Creating a
      // category is nearly always followed by filling it, and going back to
      // "All" means finding the new one in the strip before you can start.
      // Editing lands on the same category, which is the same journey.
      navigate(`/menu?category=${result.id}`);
    }
  }, [restaurantId, name, description, existing, categories.length, action, toast, t, navigate]);

  if (!isNew && !existing && menu.loading) return <LoadingState />;

  if (!isNew && !existing) {
    return (
      <Screen>
        <EmptyState
          icon={CircleHelp}
          title={t('common.notFound')}
          actionLabel={t('merchant.nav.menu')}
          onAction={back}
        />
      </Screen>
    );
  }

  return (
    <Screen
      // Pinned, so the action that commits the form is never the thing that
      // needs a scroll once the keyboard is up.
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            <Button variant="secondary" onPress={back} accessibilityLabel={t('common.cancel')}>
              <Text>{t('common.cancel')}</Text>
            </Button>
            <Button
              onPress={() => void save()}
              disabled={action.pending || !name.trim()}
              accessibilityLabel={t('common.save')}
              aria-busy={action.pending}>
              {action.pending ? (
                <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
              ) : (
                <Text>{t('common.save')}</Text>
              )}
            </Button>
          </Row>
        </FooterBar>
      }>
      <Stack gap="xl">
        <PageHeading
          title={isNew ? t('merchant.menu.categoryNew') : t('merchant.menu.categoryRename')}
        />

        <Card className="p-4">
          <Stack gap="lg">
            <TextField
              label={t('merchant.menu.categoryName')}
              required
              value={name}
              onChangeText={setName}
              placeholder={t('merchant.menu.categoryNamePlaceholder')}
              autoFocus={isNew}
              returnKeyType="next"
              onSubmitEditing={() => void save()}
            />
            <TextField
              label={t('common.description')}
              value={description}
              onChangeText={setDescription}
              placeholder={t('merchant.menu.categoryDescriptionPlaceholder')}
            />
            <Text className="text-subtle-foreground text-xs">
              {t('merchant.menu.categoryHint')}
            </Text>

            {action.error ? (
              <ErrorBanner message={action.error} onRetry={() => void save()} />
            ) : null}
          </Stack>
        </Card>
      </Stack>
    </Screen>
  );
}
